import { isPeaksLength, MAX_DURATION_MS, THUMB_CONTENT_TYPE } from "@vault/shared/media";
import { signIn } from "../../api.ts";
import { keys, queryClient } from "../queries.ts";
import type { AnalysisReply, AnalysisRequest } from "./messages.ts";
import { columnsFor, createBins } from "./peaks.ts";
import { encodeThumb, fitEdge } from "./thumbs.ts";

/** Decoding on the main thread holds the whole track as float32, so long ones only report a length. */
const DECODE_BUDGET_BYTES = 160 * 1024 * 1024;
const MEDIA_TIMEOUT_MS = 15_000;

const AUDIO_EXTENSIONS = new Set([
  "wav",
  "wave",
  "aif",
  "aiff",
  "aifc",
  "mp3",
  "flac",
  "m4a",
  "aac",
  "ogg",
  "oga",
  "opus",
  "wma",
]);

export type AnalysisTarget = { nodeId: string; version: number };

export type AnalysisKind = "audio" | "image" | "video";

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : "";
};

export const analysisKind = (file: File): AnalysisKind | null => {
  const type = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (type.startsWith("audio/") || AUDIO_EXTENSIONS.has(extensionOf(file.name))) return "audio";
  if (type.startsWith("image/") && type !== "image/svg+xml") return "image";
  if (type.startsWith("video/")) return "video";
  return null;
};

export const analysisLabel = (file: File): string | null => {
  const kind = analysisKind(file);
  if (kind === "audio") return "Reading the waveform…";
  return kind === null ? null : "Making a thumbnail…";
};

const validMs = (ms: number | null): number | null => {
  if (ms === null || !Number.isFinite(ms)) return null;
  const whole = Math.round(ms);
  return whole >= 1 && whole <= MAX_DURATION_MS ? whole : null;
};

const durationMsOf = (seconds: number | null): number | null =>
  validMs(seconds === null ? null : seconds * 1000);

const request = async (
  url: string,
  method: "PUT" | "PATCH",
  body: BodyInit,
  contentType: string,
) => {
  try {
    const response = await fetch(url, {
      method,
      body,
      credentials: "same-origin",
      headers: { "Content-Type": contentType, "X-Drive-Client": "1" },
    });
    if (response.status === 401) {
      void signIn();
      return false;
    }
    if (!response.ok) {
      console.warn(`The drive answered ${response.status} for ${method} ${url}.`);
      return false;
    }
    return true;
  } catch (error) {
    console.warn(error);
    return false;
  }
};

const versionPath = ({ nodeId, version }: AnalysisTarget) =>
  `/api/v1/nodes/${encodeURIComponent(nodeId)}/versions/${version}`;

const sendPeaks = (target: AnalysisTarget, peaks: Uint8Array<ArrayBuffer>, durationMs: number) =>
  request(
    `${versionPath(target)}/peaks?duration=${durationMs}`,
    "PUT",
    peaks,
    "application/octet-stream",
  );

const sendDuration = (target: AnalysisTarget, durationMs: number) =>
  request(versionPath(target), "PATCH", JSON.stringify({ durationMs }), "application/json");

const sendThumb = (target: AnalysisTarget, thumb: Blob) =>
  request(
    `/api/v1/nodes/${encodeURIComponent(target.nodeId)}/thumb`,
    "PUT",
    thumb,
    THUMB_CONTENT_TYPE,
  );

const sendAudio = async (
  target: AnalysisTarget,
  durationMs: number | null,
  peaks: Uint8Array<ArrayBuffer> | null,
) => {
  if (durationMs === null) return false;
  if (peaks && isPeaksLength(peaks.byteLength)) return sendPeaks(target, peaks, durationMs);
  return sendDuration(target, durationMs);
};

const runWorker = (message: AnalysisRequest) =>
  new Promise<AnalysisReply>((resolve) => {
    const worker = new Worker(new URL("./analysis.worker.ts", import.meta.url), { type: "module" });
    const finish = (reply: AnalysisReply) => {
      worker.terminate();
      resolve(reply);
    };
    worker.onmessage = (event: MessageEvent<AnalysisReply>) => finish(event.data);
    worker.onerror = (event) => finish({ ok: false, reason: "failed", message: event.message });
    worker.postMessage(message);
  });

const settle = <T>(promise: Promise<T>, fallback: T) =>
  Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), MEDIA_TIMEOUT_MS)),
  ]);

const metadataSeconds = (element: HTMLMediaElement, url: string) =>
  settle(
    new Promise<number | null>((resolve) => {
      element.addEventListener(
        "loadedmetadata",
        () => resolve(Number.isFinite(element.duration) ? element.duration : null),
        { once: true },
      );
      element.addEventListener("error", () => resolve(null), { once: true });
      element.preload = "metadata";
      element.src = url;
    }),
    null,
  );

const decodedPeaks = async (file: File) => {
  if (typeof OfflineAudioContext === "undefined") return null;
  const context = new OfflineAudioContext(1, 1, 48000);
  const buffer = await context.decodeAudioData(await file.arrayBuffer()).catch((error: unknown) => {
    console.warn(error);
    return null;
  });
  if (!buffer) return null;
  const columns = columnsFor(buffer.length);
  if (columns === 0) return { durationMs: durationMsOf(buffer.duration), peaks: null };
  const bins = createBins(buffer.length, columns);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const samples = buffer.getChannelData(channel);
    for (let index = 0; index < samples.length; index += 1) {
      bins.add(index, Math.abs(samples[index] ?? 0));
    }
  }
  return { durationMs: durationMsOf(buffer.duration), peaks: bins.bytes() };
};

const analyseAudio = async (file: File, target: AnalysisTarget) => {
  const reply = await runWorker({ job: "peaks", file });
  if (reply.ok && reply.job === "peaks") {
    return sendAudio(target, validMs(reply.durationMs), reply.peaks);
  }
  if (!reply.ok && reply.reason === "failed") console.warn(reply.message);

  const url = URL.createObjectURL(file);
  try {
    const seconds = await metadataSeconds(new Audio(), url);
    const durationMs = durationMsOf(seconds);
    if (durationMs === null) return false;
    if (seconds !== null && seconds * 48000 * 2 * 4 <= DECODE_BUDGET_BYTES) {
      const decoded = await decodedPeaks(file);
      if (decoded?.peaks) return sendAudio(target, decoded.durationMs ?? durationMs, decoded.peaks);
    }
    return sendDuration(target, durationMs);
  } finally {
    URL.revokeObjectURL(url);
  }
};

const canvasThumb = (
  draw: (context: CanvasRenderingContext2D) => void,
  width: number,
  height: number,
) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.imageSmoothingQuality = "high";
  try {
    draw(context);
  } catch (error) {
    console.warn(error);
    return null;
  }
  return encodeThumb(
    (quality) =>
      new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, THUMB_CONTENT_TYPE, quality);
      }),
  );
};

const analyseImage = async (file: File, target: AnalysisTarget) => {
  const reply = await runWorker({ job: "thumb", file });
  if (reply.ok && reply.job === "thumb") return sendThumb(target, reply.thumb);
  if (!reply.ok && reply.reason === "failed") console.warn(reply.message);

  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }).catch(
    () => null,
  );
  if (!bitmap) return false;
  const size = fitEdge(bitmap.width, bitmap.height);
  const thumb = await canvasThumb(
    (context) => context.drawImage(bitmap, 0, 0, size.width, size.height),
    size.width,
    size.height,
  );
  bitmap.close();
  return thumb ? sendThumb(target, thumb) : false;
};

const seekedFrame = (video: HTMLVideoElement, seconds: number) =>
  settle(
    new Promise<boolean>((resolve) => {
      video.addEventListener("seeked", () => resolve(true), { once: true });
      video.addEventListener("error", () => resolve(false), { once: true });
      video.currentTime = Math.min(1, seconds / 10);
    }),
    false,
  );

const analyseVideo = async (file: File, target: AnalysisTarget) => {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  const url = URL.createObjectURL(file);
  try {
    const durationMs = durationMsOf(await metadataSeconds(video, url));
    const stored = durationMs === null ? false : await sendDuration(target, durationMs);
    if (durationMs === null || !(await seekedFrame(video, durationMs / 1000))) return stored;
    if (video.videoWidth === 0 || video.videoHeight === 0) return stored;
    const size = fitEdge(video.videoWidth, video.videoHeight);
    const thumb = await canvasThumb(
      (context) => context.drawImage(video, 0, 0, size.width, size.height),
      size.width,
      size.height,
    );
    return thumb ? await sendThumb(target, thumb) : stored;
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
};

const analyse = async (file: File, target: AnalysisTarget) => {
  const kind = analysisKind(file);
  if (kind === null) return;
  try {
    const stored =
      kind === "audio"
        ? await analyseAudio(file, target)
        : kind === "image"
          ? await analyseImage(file, target)
          : await analyseVideo(file, target);
    if (!stored) return;
    void queryClient.invalidateQueries({ queryKey: keys.node(target.nodeId) });
    void queryClient.invalidateQueries({ queryKey: keys.versions(target.nodeId) });
  } catch (error) {
    console.warn(error);
  }
};

let queue: Promise<void> = Promise.resolve();

/** One file at a time: two large tracks analysed at once would double the decoded audio in memory. */
export const queueAnalysis = (file: File, target: AnalysisTarget): Promise<void> => {
  queue = queue.then(() => analyse(file, target));
  return queue;
};
